import { Avatar, Icon, Menu } from "@obsina/design-system";
import {
  ChevronDown,
  Library,
  LogOut,
  Network,
  Plug,
  SlidersHorizontal,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { AuthenticatedAccount } from "./App";
import type { AppMessages } from "./messages";

/** The control plane, the read-only, evidence-backed Memory Graph, the
 * collected-data library and the assistant connection surface are the top-level
 * destinations (the graph was brought into scope by Erkan's 2026-07-17
 * decision). Chat and general search remain deliberately not navigable here.
 *
 * `commitments` is still a view — the screen and its data path are kept intact —
 * but it has no sidebar item while claim extraction is off: every row on it came
 * from an extracted claim, so navigating there would only ever reach a
 * permanently empty list. It renders the closed-for-this-phase surface instead;
 * restoring the destination is putting its item back in the list below. */
export type AppView =
  | "control_plane"
  | "commitments"
  | "memory_graph"
  | "sources"
  | "integrations"
  // Reached only by an assistant sending the browser here to approve a
  // connection, never from the sidebar: it is a step inside someone else's
  // flow, not a place to go. It is a member of this union so the shell can
  // render it and so `aria-current` stays honest — no sidebar item matches it.
  | "connect_assistant";

export function PrimaryNav({
  activeView,
  collapsed,
  onNavigate,
  messages,
}: {
  activeView: AppView;
  collapsed: boolean;
  onNavigate: (view: AppView) => void;
  messages: AppMessages;
}) {
  const items: readonly [AppView, string, LucideIcon][] = [
    ["control_plane", messages.nav.controlPlane, SlidersHorizontal],
    ["memory_graph", messages.nav.memoryGraph, Network],
    ["sources", messages.nav.sources, Library],
    ["integrations", messages.nav.integrations, Plug],
  ];
  return (
    <nav aria-label={messages.nav.label} className="app-nav">
      {items.map(([view, label, icon]) => (
        <button
          aria-current={activeView === view ? "page" : undefined}
          aria-label={label}
          className="app-nav__item"
          key={view}
          onClick={() => onNavigate(view)}
          title={collapsed ? label : undefined}
          type="button"
        >
          <Icon icon={icon} size={17} />
          <span className="app-nav__label">{label}</span>
        </button>
      ))}
    </nav>
  );
}

export function AccountMenu({
  account,
  collapsed,
  messages,
  signingOut,
  onSignOut,
}: {
  account: AuthenticatedAccount;
  collapsed: boolean;
  messages: AppMessages;
  signingOut: boolean;
  onSignOut: () => void;
}) {
  const accountLabel = account.email ?? account.name;
  return (
    <Menu
      className="account-menu"
      data-sidebar-collapsed={collapsed || undefined}
      items={[
        {
          id: "sign-out",
          label: signingOut
            ? messages.account.signingOut
            : messages.account.signOut,
          icon: LogOut,
          danger: true,
          disabled: signingOut,
          onSelect: onSignOut,
        },
      ]}
      label={messages.account.menuLabel}
      trigger={
        <span className="account-menu__trigger">
          <Avatar name={account.name} size={30} />
          <span className="account-menu__identity">
            <strong>{account.name}</strong>
            {account.email && <span>{account.email}</span>}
          </span>
          <Icon
            className="account-menu__chevron"
            icon={ChevronDown}
            size={15}
          />
          <span className="obsina-sr-only">{accountLabel}</span>
        </span>
      }
    />
  );
}
